
import { ISerialHistoryFullDataItem } from '@/apis/serialAPI'
import BarcodeSymbologiesSelection from '@/components/BarcodeSymbologiesSelection.vue'
import ConfirmDialog from '@/components/dialog/ConfirmDialog.vue'
import SelectionCameraPopup from '@/components/dialog/SelectionCameraPopup.vue'
import KeyenceHTHardKeyButton from '@/components/keyenceHT/hardkeys/KeyenceHTHardKeyButton.vue'
import KeyenceHTListStringButton from '@/components/keyenceHT/ListStringScanButton.vue'
import KeyenceHTScannerConfig from '@/components/keyenceHT/ScannerConfig.vue'
import ListStringScanComponent from '@/components/ListStringScanComponent.vue'
import IconSwitch from '@/components/svg/IconSwitch.vue'
import IconTrolley from '@/components/svg/IconTrolley.vue'
import ScanArea from '@/components/svg/ScanArea.vue'
import {
  CHECK_SERIAL_DETAIL,
  CHECK_SERIAL_LAST_HISTORY_DETAIL,
  GET_SERIAL_HISTORIES,
  OPEN_DIALOG,
} from '@/store/actions'
import {
  ALLOWED_SERIAL_PREFIX,
  DATE_TIME,
  getSerialErrorMessage,
  LEFT_AMOUNT_START_TROLLEY_INDEX,
  POSITIVE_DECIMAL_REGEX,
  DEFAULT_DEBOUNCE_TIME,
  UNIT_OPTIONS,
} from '@/utils/constants'
import { EOperatorType } from '@/utils/enums'
import errorHandler from '@/utils/errorHandler'
import { logError } from '@/utils/errorLogger'
import { openMessage, trollyNumberLimit } from '@/utils/helpers'
import { KeyenceHardKey } from '@/utils/keyenceHT/keyenceHardKeyEventHandler'
import { isKeyenceEnterpriseBrowser } from '@/utils/keyenceHT/keyenceModules'
import { unsubmittedSerialCodes } from '@/utils/serialChanges'
import { IListStringItem, TError } from '@/utils/types/commonTypes'
import { format } from 'date-fns'
import debounce from 'lodash/debounce'
import { Options, Vue } from 'vue-class-component'
import { Emit, Prop } from 'vue-property-decorator'

@Options({
  components: {
    BarcodeSymbologiesSelection,
    ConfirmDialog,
    SelectionCameraPopup,
    IconSwitch,
    IconTrolley,
    ScanArea,
    ListStringScanComponent,
    KeyenceHTListStringButton,
    KeyenceHTScannerConfig,
    KeyenceHTHardKeyButton,
  },
  emits: ['update:modelValue', 'change', 'removedListString'],
  inheritAttrs: false,
  name: 'InputListString',
})
export default class InputListString extends Vue {
  @Prop({ type: Array, default: () => [] }) modelValue?: IListStringItem[]
  @Prop({ type: Boolean, default: false }) isScan?: boolean
  @Prop({ type: String, default: '' }) fieldSchema?: string
  @Prop({ type: Number }) readonly minLength?: number
  @Prop({ type: Number }) readonly maxLength?: number
  @Prop({ type: String }) readonly fieldName?: string
  @Prop({ type: String }) readonly error?: string
  @Prop({ type: String }) readonly operatorType?: EOperatorType

  dialogVisible = false
  loading = false
  localValue: IListStringItem[] = []
  deletedSerialData: IListStringItem[] = []

  inputValue = ''

  // Scan mode state
  scanDialogVisible = false
  scanLoading = false
  manualScanValue = ''

  // Camera and barcode scanner state
  isReadyToScan = true
  scannedDialogVisible = false
  checkScannedBarcode = false
  scannedBarcodeList: IListStringItem[] = []
  isConfirmDeleteDialogVisible = false
  isHandlingAddScan = false
  deleteTargetCode = ''

  // Scan mode state
  scanMode: 'add' | 'remove' = 'add'

  KeyenceHardKey = KeyenceHardKey

  mounted() {
    this.localValue = [...(this.modelValue || [])]
  }

  get forms() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.trackPointForms
  }

  get project() {
    return this.$store.state.project.details
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get currentManufacturingPlan() {
    return this.$store.state.barcode.manufacturingPlan
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get listValue(): IListStringItem[] {
    return this.localValue || []
  }

  get isOperatorTypeUse(): boolean {
    return this.operatorType === EOperatorType.USE
  }

  get isOperatorTypeAdd(): boolean {
    return this.operatorType === EOperatorType.ADD
  }

  get screenNameKey(): string {
    switch (this.operatorType) {
      case EOperatorType.ADD:
        return 'operator_screen_produce'
      case EOperatorType.USE:
        return 'operator_screen_use'
      case EOperatorType.CORRECT_VALUE:
        return 'operator_screen_left_amount'
      default:
        return ''
    }
  }

  get dialogModeClass(): string {
    switch (this.operatorType) {
      case EOperatorType.ADD:
        return 'mode-operator-add'
      case EOperatorType.USE:
        return 'mode-operator-use'
      case EOperatorType.CORRECT_VALUE:
        return 'mode-left-amount-collection'
      default:
        return ''
    }
  }

  get isOperatorTypeCollect(): boolean {
    return this.operatorType === EOperatorType.CORRECT_VALUE
  }

  get validationError(): string {
    const length = this.listValue.length

    if (this.error) {
      return this.error
    }

    if (this.minLength !== undefined && length < this.minLength) {
      return this.$t('minimum_items_required', { minLength: this.minLength })
    }

    if (this.maxLength !== undefined && length > this.maxLength) {
      return this.$t('maximum_items_required', { maxLength: this.maxLength })
    }

    return ''
  }

  get hasValidationError(): boolean {
    return !!this.validationError
  }

  get wouldExceedMaxLength(): boolean {
    return this.maxLength !== undefined && this.localValue.length >= this.maxLength
  }

  get totalAmount(): number {
    return this.listValue.reduce((sum, item) => sum + (item.amount || 0), 0)
  }

  get currentItemCount(): number {
    return this.listValue.length || 0
  }

  get sentSerialList(): IListStringItem[] {
    const fieldKey = (this.$attrs.name as string) || 'sirial'
    return (this.previousTrackingData?.customFields?.[fieldKey]?.listString as IListStringItem[]) || []
  }

  get sentItemCount(): number {
    return this.sentSerialList.length
  }

  get scannedItemCount(): number {
    return unsubmittedSerialCodes(this.sentSerialList, this.listValue, this.deletedSerialData).length
  }

  get producedSerialTotalNumber(): number {
    return this.barcode?.serialSetting.producedSerialTotalNumber || 0
  }

  get maxItemCount(): number {
    return this.barcode?.serialSetting.producedSerialMaximumTotalNumber || 0
  }

  get defaultUnit(): string {
    return this.unitOptions[0]?.value || ''
  }

  get stockUnit(): string {
    return this.barcode?.serialSetting.stockUnit
  }

  get defaultAmount(): number {
    return 0
  }

  get defaultLimitAmount(): number {
    return 10
  }

  @Emit('update:modelValue')
  updateModelValue(newList: IListStringItem[]) {
    return newList
  }

  @Emit('change')
  emitChange(newList: IListStringItem[]) {
    return newList
  }

  @Emit('removedListString')
  emitRemovedListString(removed: IListStringItem[]) {
    return removed
  }

  get unitOptions() {
    return UNIT_OPTIONS.map((unit) => ({ label: unit, value: unit }))
  }

  addItem(e: Event) {
    e.preventDefault()
    if (this.inputValue.trim() && !this.wouldExceedMaxLength) {
      const code = this.inputValue.trim()
      if (!this.validateCodePrefix(code)) {
        const invalidCodeLabel = this.$t('invalid_code_prefix')
        openMessage(invalidCodeLabel, 'error')
        logError('', {}, '', invalidCodeLabel)
        return
      }
      const newItem = this.createNewSerialItem({ code })
      this.removeFromDeletedSerialData(newItem)
      this.localValue = [...this.localValue, newItem]
      this.inputValue = ''
    }
  }

  private removeFromDeletedSerialData(item: IListStringItem): void {
    const index = this.deletedSerialData.findIndex(
      (deleted) => deleted.code === item.code && deleted.id === item.id
    )
    if (index !== -1) {
      this.deletedSerialData.splice(index, 1)
    }
  }

  removeItem(index: number) {
    this.localValue = this.localValue.filter((_, i) => i !== index)
  }

  updateItemAmount(index: number, value: string | number) {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value
    if (!isNaN(numericValue) && numericValue >= 0) {
      this.localValue[index].amount = numericValue
    }
  }

  created() {
    this.warnAmountInput = debounce(this.warnAmountInput.bind(this), DEFAULT_DEBOUNCE_TIME)
  }

  warnAmountInput(index: number, raw: string) {
    const item = this.localValue[index]
    if (!item || !raw) return

    if (!POSITIVE_DECIMAL_REGEX.test(raw.trim())) {
      openMessage(this.$t('amount_invalid_format'), 'warning')
      return
    }

    const parsed = parseFloat(raw)
    if (item.limitAmount !== undefined && parsed > item.limitAmount) {
      openMessage(
        this.$t('amount_exceeds_limit', {
          limit: item.limitAmount,
          unit: (item.unit || '').toUpperCase(),
        }),
        'warning'
      )
    }
  }

  onAmountInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement | null
    if (input?.tagName === 'INPUT') {
      this.warnAmountInput(index, input.value)
    }
  }

  confirmChanges() {
    this.updateModelValue(this.localValue)
    this.emitChange(this.localValue)
    this.emitRemovedListString(this.deletedSerialData)

    this.dialogVisible = false
  }

  async cancelChanges() {
    const hasChanges = this.hasUnsavedChanges()

    if (hasChanges) {
      const changesSummary = this.getChangesSummary()

      try {
        await this.$confirm(
          this.$t('unsaved_changes', { changes: changesSummary }),
          this.$t('title_unsaved_changes', { changes: changesSummary }),
          {
            confirmButtonText: this.$t('discard_changes'),
            cancelButtonText: this.$t('keep_editing'),
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        )
      } catch (error) {}
    }
    this.resetToOriginalValues()
  }

  private hasUnsavedChanges(): boolean {
    return JSON.stringify(this.localValue) !== JSON.stringify(this.modelValue || [])
  }

  private getChangesSummary(): string {
    const originalLength = this.modelValue?.length || 0
    const currentLength = this.localValue.length

    if (originalLength !== currentLength) {
      return ` (${originalLength} → ${currentLength} items)`
    }
    return ` (${currentLength} items modified)`
  }

  private resetToOriginalValues() {
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = false
  }

  openDialog(mode: 'add' | 'remove') {
    this.scanMode = mode
    this.localValue = [...(this.modelValue || [])]
    this.dialogVisible = true

    if (this.localValue.length > 0) {
      this.scrollToBottom()
    }
  }

  private scrollToBottom() {
    this.$nextTick(() => {
      const container = this.$refs.listContainer as HTMLElement
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    })
  }

  // Scan mode methods
  openScanDialog() {
    this.scannedBarcodeList = []
    this.manualScanValue = ''
    this.scanDialogVisible = true
  }

  async onScanConfirm(scannedValue: string) {
    if (!scannedValue.trim() || this.isScanBlocked) return

    const trimmedScannedValue = scannedValue.trim()

    if (this.scanMode === 'add') {
      await this.handleScanAdd(trimmedScannedValue)
    } else {
      await this.handleScanRemove(trimmedScannedValue)
    }

    this.onConfirmListSerialCodes()
  }

  private validateScanAdd(scannedValue: string): boolean {
    if (this.wouldExceedMaxLength) {
      this.isHandlingAddScan = false
      const maximumItemLabel = this.$t('maximum_items_reached', { maxItems: this.maxLength })
      openMessage(maximumItemLabel, 'warning')
      logError('', {}, '', maximumItemLabel)
      return false
    }

    if (!this.isOperatorTypeUse && !this.validateCodePrefix(scannedValue)) {
      this.isHandlingAddScan = false
      logError('', {}, '', this.$t('invalid_code_prefix'))
      this.handleSerialPrefixError(scannedValue)
      return false
    }

    if (scannedValue.toLowerCase().startsWith('false')) {
      this.isHandlingAddScan = false
      // openMessage(this.$t('invalid_code_prefix'), 'warning')
      return false
    }

    const existingItem = this.localValue.find((item) => item.code === scannedValue)
    if (existingItem && this.isOperatorTypeAdd) {
      openMessage(this.$t('item_already_scanned'), 'warning')
      this.isHandlingAddScan = false
      return false
    }

    return true
  }

  private async handleScanAdd(scannedValue: string) {
    this.isHandlingAddScan = true

    if (!this.validateScanAdd(scannedValue)) return

    if (this.operatorType) {
      const validatedData = await this.validateSerialCode(scannedValue, this.operatorType)
      if (!validatedData) return
      this.addSerialItemToList(validatedData)
    }
    const item = this.localValue.find((item) => item.code === scannedValue)
    const index = this.localValue.findIndex((item) => item.code === scannedValue)
    const content = this.formatItemContent(item)
    this.isHandlingAddScan = false
    if (item?.warnings && item.warnings.length > 0) return
    openMessage(
      this.$t('item_detected', {
        index: index + 1,
        item: content,
      }),
      'success'
    )
  }

  private formatItemContent(item: IListStringItem | undefined): string {
    if (!item) return ''
    const parts = [item.code]
    if (item.itemCode) {
      parts.push(item.itemCode)
    }
    if (item.lot) {
      parts.push(`LOT: ${item.lot}`)
    }
    return parts.join(' - ')
  }

  private async handleScanRemove(scannedValue: string) {
    const reversedIndex = this.localValue
      .slice()
      .reverse()
      .findIndex((item) => item.code === scannedValue)

    if (reversedIndex === -1) return

    this.deleteTargetCode = scannedValue
    this.isConfirmDeleteDialogVisible = true
  }

  private onConfirmDelete() {
    const reversedIndex = this.localValue
      .slice()
      .reverse()
      .findIndex((item) => item.code === this.deleteTargetCode)

    if (reversedIndex === -1) {
      this.deleteTargetCode = ''
      return
    }

    const index = this.localValue.length - 1 - reversedIndex
    const deletedItem = { ...this.localValue[index], scannedDateTime: new Date().toISOString() }
    this.deletedSerialData.push(deletedItem)
    this.localValue.splice(index, 1)
    this.updateTrolleyIndexes()
    openMessage(this.$t('item_deleted'), 'success')
    this.deleteTargetCode = ''
  }

  private onCancelDelete() {
    this.deleteTargetCode = ''
  }

  private updateTrolleyIndexes() {
    if (!this.operatorType) return

    this.localValue.forEach((item, index) => {
      if (this.operatorType === EOperatorType.CORRECT_VALUE) {
        item.trollyIndex = LEFT_AMOUNT_START_TROLLEY_INDEX + index
        delete item.trollyNumber
      } else {
        const newTrolleyIndex = index + 1
        item.trollyIndex = newTrolleyIndex
        item.trollyNumber = Math.ceil(newTrolleyIndex / trollyNumberLimit(this.localValue))
      }
    })
  }

  get previousTrackingData() {
    return this.$store.getters.trackingData
  }

  private isAllowDuplicatedSerial(serial: string): boolean {
    if (!this.localValue) {
      return false
    }

    return !this.localValue.some((item) => item.code === serial && item.producedSerialHistoryId)
  }

  private async validateSerialCode(code: string, operatorType: EOperatorType): Promise<IListStringItem | null> {
    try {
      this.loading = true
      const data: IListStringItem = await this.$store.dispatch(CHECK_SERIAL_DETAIL, {
        operatorType: operatorType,
        code,
        isEdit: !!this.previousTrackingData,
      })

      if (!this.isOperatorTypeAdd && !this.isAllowDuplicatedSerial(data.code)) {
        const duplicatedSerialError = this.$t('item_already_scanned')
        openMessage(duplicatedSerialError, 'warning')
        logError('', {}, '', duplicatedSerialError)

        return null
      }

      return data
    } catch (error) {
      if (this.isOperatorTypeUse && (error === '3110' || error === '3106')) {
        this.showManufacturingResultNotRegisteredError(code)
        return null
      }
      if (error === '3106' && (await this.handleAmountInsufficientError(code))) {
        return null
      }

      if (error === '3114') {
        await this.handleSerialUsageError(code)
        return null
      }

      if (error === '3123') {
        await this.handleExpirationError(code)
        return null
      }

      if (this.handleExpiredProductError(error)) {
        return null
      }
      errorHandler(error as TError)
      return null
    } finally {
      this.loading = false
      this.isHandlingAddScan = false
    }
  }

  get visibleErrorDialog() {
    return this.$store.state.dialogVisible
  }

  get isScanBlocked() {
    return this.isHandlingAddScan || this.isConfirmDeleteDialogVisible || this.visibleErrorDialog
  }

  private handleExpiredProductError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) {
      return false
    }

    const errorObj = error as Record<string, string[]>
    const errorKey = Object.keys(errorObj)[0]
    const errorCodes = errorObj[errorKey]

    if (!errorCodes) {
      return false
    }

    if (this.isOperatorTypeUse && (errorCodes.includes('3110') || errorCodes.includes('3106'))) {
      this.showManufacturingResultNotRegisteredError(errorKey.charAt(0).toUpperCase() + errorKey.slice(1))
      return true
    }

    const errorCode = errorCodes.find((code) => getSerialErrorMessage(code, errorKey))
    if (!errorCode) {
      return false
    }

    this.$store.dispatch(OPEN_DIALOG, {
      title: this.$t('el.messagebox.error'),
      message: getSerialErrorMessage(errorCode, errorKey),
    })

    return true
  }

  private async handleAmountInsufficientError(code: string): Promise<boolean> {
    try {
      const {
        serialHistories,
      }: { serialHistories: ISerialHistoryFullDataItem[] } = await this.$store.dispatch(GET_SERIAL_HISTORIES, { code })
      const currentManufacturingPlanId = this.currentManufacturingPlan?.id
      const useHistories = (serialHistories || []).filter((history) => history.operatorType === EOperatorType.USE)

      if (useHistories.some((history) => history.manufacturingPlanId === currentManufacturingPlanId)) {
        const duplicatedSerialError = this.$t('serial_already_registered_as_tracking_data')
        openMessage(duplicatedSerialError, 'warning')
        logError('', {}, '', duplicatedSerialError)
        return true
      }

      if (useHistories.some((history) => history.manufacturingPlanId !== currentManufacturingPlanId)) {
        const usedInAnotherOrderError = this.$t('serial_used_in_another_order')
        openMessage(usedInAnotherOrderError, 'warning')
        logError('', {}, '', usedInAnotherOrderError)
        return true
      }
    } catch (error) {
      errorHandler(error as TError)
      return true
    }

    return false
  }

  private showManufacturingResultNotRegisteredError(code: string): void {
    const notRegisteredError = this.$t('manufacturing_result_not_registered')
    const errorDetail = `<div style="font-weight: bold; text-align: center;">
      <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
        ${this.$t('usage_prohibited')}
      </div>
      <div style="color: red;">
        <div>${notRegisteredError}</div>
        <div style="margin-top: 20px;">(${code})</div>
      </div>
    </div>`

    this.$store.dispatch(OPEN_DIALOG, {
      title: this.$t('confirm_usage_item'),
      message: errorDetail,
    })
    logError('', {}, '', notRegisteredError + code)
  }

  private async handleSerialUsageError(code: string): Promise<boolean> {
    try {
      const lastHistoryDetail = await this.$store.dispatch(CHECK_SERIAL_LAST_HISTORY_DETAIL, { code })
      const { itemName, itemCode } = this.currentManufacturingPlan
      const { itemName: serialItemName, itemCode: serialItemCode } = lastHistoryDetail

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemCode}</div>
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${serialItemCode}</div>
          <div>${serialItemName}</div>
          <div>(${code})</div>
          <div>${this.$t('serial_already_input_error')}</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      errorHandler(error as TError)
    }
    return false
  }

  private async handleSerialPrefixError(code: string): Promise<boolean> {
    try {
      const { itemName, itemCode } = this.currentManufacturingPlan
      const requiredPrefix = itemCode?.startsWith('4') ? ALLOWED_SERIAL_PREFIX.BT : ALLOWED_SERIAL_PREFIX.TP
      const prefixMessage = this.$t('no_registered_data_for_qr', { prefix: requiredPrefix })

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${prefixMessage}</div>
          <div style="margin-top: 20px;">(${code})</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      errorHandler(error as TError)
    }
    return false
  }

  private async handleExpirationError(code: string): Promise<boolean> {
    try {
      const lastHistoryDetail = await this.$store.dispatch(CHECK_SERIAL_LAST_HISTORY_DETAIL, { code })
      const { itemName, itemCode } = this.currentManufacturingPlan
      const { itemName: serialItemName, itemCode: serialItemCode, expiredDateTime } = lastHistoryDetail

      const errorDetail = `<div style="font-weight: bold; text-align: center;">
        <div style="color: blue;">
          <div>${itemCode}</div>
          <div>${itemName}</div>
        </div>
        <div style="background-color: red; color: white; margin: 10px 0; padding: 5px;">
          ${this.$t('usage_prohibited')}
        </div>
        <div style="color: red;">
          <div>${this.$t('qr_expired_cannot_use_for_manufacturing')}</div>
          <div style="margin-top: 20px;">${serialItemCode} : ${serialItemName}</div>
          <div style="margin-top: 20px;"">${this.$t('expiration_date')} : ${format(
        new Date(expiredDateTime),
        DATE_TIME.DISPLAY_FORMAT
      )} </div>
          <div style="margin-top: 20px;">(${code})</div>
        </div>
      </div>`

      this.$store.dispatch(OPEN_DIALOG, {
        title: this.$t('confirm_usage_item'),
        message: errorDetail,
      })
    } catch (error) {
      errorHandler(error as TError)
    }
    return false
  }

  private addSerialItemToList(data: Partial<IListStringItem>): void {
    const newItem = this.createNewSerialItem(data)
    if (this.operatorType === EOperatorType.ADD) {
      newItem.id = 0
    }
    if (newItem.warnings && newItem.warnings.length > 0) {
      const messages = newItem.warnings.map((warning) => `[${warning}]: ${this.$t(`errors.${warning}`)}`)
      const message = messages.join('<br>')
      openMessage(message, 'warning', 5000, {
        dangerouslyUseHTMLString: true,
      })
      logError('', {}, '', message)
    }

    this.removeFromDeletedSerialData(newItem)
    this.localValue = [...this.localValue, newItem]
    this.scrollToBottom()
  }

  private validateCodePrefix(code: string): boolean {
    if (this.localValue.length === 0) {
      return code.startsWith(ALLOWED_SERIAL_PREFIX.TP) || code.startsWith(ALLOWED_SERIAL_PREFIX.BT)
    }

    const firstCodePrefix = this.localValue[0]?.code?.substring(0, 2)
    const newCodePrefix = code.substring(0, 2)

    return firstCodePrefix === newCodePrefix
  }

  get producedLastTrollyIndex(): number {
    return this.barcode?.serialSetting.producedLastTrollyIndex
  }

  get producedLastTrollyNumber(): number {
    return this.barcode?.serialSetting.producedLastTrollyNumber
  }

  lastTrollyNumber(operatorType: EOperatorType): number {
    switch (operatorType) {
      case EOperatorType.ADD:
        return this.barcode?.serialSetting.producedLastTrollyNumber ?? 0
      case EOperatorType.USE:
        return this.barcode?.serialSetting.usedLastTrollyNumber ?? 0
      default:
        return 0
    }
  }

  lastTrollyIndex(operatorType: EOperatorType): number {
    switch (operatorType) {
      case EOperatorType.ADD:
        return this.barcode?.serialSetting.producedLastTrollyIndex
      case EOperatorType.USE:
        return this.barcode?.serialSetting.usedLastTrollyIndex
      default:
        return this.barcode?.serialSetting.correctValueLastTrollyIndex
    }
  }

  handleGetTrollyInfo(operatorType: EOperatorType) {
    if (this.localValue.length === 0) {
      return {
        trolleyNumber: this.lastTrollyNumber(operatorType),
        trolleyIndex: this.lastTrollyIndex(operatorType),
      }
    }

    const lastItem = this.localValue[this.localValue.length - 1]
    const currentTrolleyIndex = lastItem.trollyIndex || this.lastTrollyIndex(operatorType) || 1

    const newTrolleyIndex = currentTrolleyIndex + 1
    const newTrolleyNumber = Math.ceil(newTrolleyIndex / trollyNumberLimit(this.localValue))

    return {
      trolleyNumber: operatorType === EOperatorType.CORRECT_VALUE ? 0 : newTrolleyNumber,
      trolleyIndex: newTrolleyIndex,
    }
  }

  createNewSerialItem(item: Partial<IListStringItem>): IListStringItem {
    if (!this.operatorType) {
      throw new Error('Operator type is required to create a new item.')
    }
    const { trolleyNumber, trolleyIndex } = this.handleGetTrollyInfo(this.operatorType)

    return {
      ...item,
      itemCode: item.itemCode ?? '',
      code: item.code || '',
      limitAmount: item.limitAmount ?? this.defaultLimitAmount,
      amount: item.initAmount ?? this.defaultAmount,
      currentAmount: item.currentAmount ?? this.defaultAmount,
      unit: item.unit ?? this.defaultUnit,
      scannedDateTime: item.scannedDateTime ?? new Date().toISOString(),
      warnings: item.warnings ?? [],
      trollyNumber: trolleyNumber,
      trollyIndex: trolleyIndex,
    } as IListStringItem
  }

  onConfirmListSerialCodes() {
    this.updateModelValue(this.localValue)
    this.emitChange(this.localValue)
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.scannedDialogVisible = true
  }

  onCloseScannedDialog() {
    this.scannedDialogVisible = false
    this.checkScannedBarcode = false
  }

  get isKeyenceEnterpriseBrowser(): boolean {
    return isKeyenceEnterpriseBrowser()
  }

  isItemEditable(index: number): boolean {
    return this.isOperatorTypeCollect || (!this.isOperatorTypeUse && index === this.listValue.length - 1)
  }
}
